import { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { BsInfoCircle } from "react-icons/bs";
import "./HelpTip.scss";

/* Tooltips are rendered into a layer of their own rather than straight into
   <body>.

   Dropped into the body, a card placed near the right-hand edge makes the
   document wider than the window. The page grows a scrollbar, the viewport
   narrows, the card moves, the scrollbar goes, the card moves back — it
   oscillates for as long as the pointer rests on the control. Asking Popper
   for viewport positioning was not enough on its own to stop it.

   The layer is fixed to the viewport and clips, so nothing placed inside it
   can change the size of the page however Popper decides to lay a card out.
   It takes no pointer events, so it never swallows a click meant for the page
   underneath — and a card drifting under the cursor does not read as the
   pointer having left the control it explains. */
let tooltipLayer = null;

export const getTooltipLayer = () => {
  if (typeof document === "undefined") {
    return undefined;
  }
  if (tooltipLayer && document.body.contains(tooltipLayer)) {
    return tooltipLayer;
  }
  tooltipLayer = document.createElement("div");
  tooltipLayer.className = "help-tip-layer";
  document.body.appendChild(tooltipLayer);
  return tooltipLayer;
};

/* Positioned against the viewport rather than the document.

   These cards are rendered into <body>, and an absolutely positioned one near
   the right-hand edge makes the document wider than the window. The page then
   grows a horizontal scrollbar, which narrows the viewport, which moves the
   card, which removes the scrollbar again — a flicker for as long as the
   pointer rests there. A fixed card cannot extend the scrollable area, so
   there is nothing to oscillate. `preventOverflow` keeps it off the edge
   itself, with a little room to spare. */
export const TOOLTIP_POPPER_CONFIG = {
  strategy: "fixed",
  modifiers: [{ name: "preventOverflow", options: { padding: 8 } }],
};

// Keep the page to one contextual tooltip at a time. Independent
// OverlayTriggers otherwise allow a focused control and a newly hovered
// control to leave two help cards open together.
let activeTooltipController = null;

/**
 * Hover/focus help for a field, an option or an icon button.
 *
 * Two shapes, one component:
 *  - no children — renders the small info icon meant to sit beside a label;
 *  - with children — wraps the control itself, so hovering the button or the
 *    field explains it without an extra icon on screen.
 *
 * The trigger is always a `span` rather than the control: a disabled button
 * fires no pointer events of its own, and a wrapper is the only target that
 * still reacts. `tabIndex` keeps the text reachable from the keyboard, so the
 * help is never mouse-only.
 */
const HelpTip = ({
  text,
  placement = "top",
  className = "",
  iconSize = 13,
  wrapperClassName = "",
  overlayClassName = "",
  children = null,
}) => {
  const generatedId = useId();
  const [show, setShow] = useState(false);
  const tooltipController = useRef(null);
  const targetRef = useRef(null);

  const hideTooltip = () => {
    setShow(false);
    if (activeTooltipController === tooltipController.current) {
      activeTooltipController = null;
    }
  };

  tooltipController.current = hideTooltip;

  const showTooltip = () => {
    if (
      activeTooltipController &&
      activeTooltipController !== tooltipController.current
    ) {
      activeTooltipController();
    }
    activeTooltipController = tooltipController.current;
    setShow(true);
  };

  /* Clicking a control focuses it, and focus is one of the things that opens
     the tooltip — so pressing a button re-opened its own help, and the card
     was left standing over whatever the press had just opened. The pointer
     was already gone by then, so nothing was coming to close it.

     Focus opens the tooltip only when it did not come from a pointer, which
     is the distinction :focus-visible draws: a keyboard user still gets the
     help, a mouse user gets the thing they clicked. A press closes it. */
  const pointerFocus = useRef(false);

  /* Everything that opens or closes the tooltip comes through here — our own
     handlers below and OverlayTrigger's built-in hover and focus triggers,
     which raise it themselves. The guard has to sit at the join: putting it
     only on our onFocus left OverlayTrigger's to reopen the card anyway. */
  const handleToggle = (nextShow) => {
    if (!nextShow) {
      hideTooltip();
      return;
    }
    if (pointerFocus.current) {
      return;
    }
    showTooltip();
  };

  /* Hovering is a fresh approach to the control, whatever happened before it,
     so it clears the press. Without this a control kept its help suppressed
     for as long as it held focus. */
  const handleMouseEnter = () => {
    pointerFocus.current = false;
    showTooltip();
  };

  const handleMouseDown = () => {
    pointerFocus.current = true;
    hideTooltip();
  };

  const handleBlur = () => {
    pointerFocus.current = false;
    hideTooltip();
  };

  /* A card must not outlive the pointer that opened it.

     mouseleave is how it normally learns the pointer has gone, and most of the
     time that is enough. It is not reliable enough to be the only thing
     holding the card open, though: a drawer or a modal opening under the
     cursor can take the pointer off the control without the control hearing
     it, and a button that disables or replaces itself on click stops firing
     pointer events at the moment it matters. Any one of those leaves a card on
     screen with nothing left that would close it.

     So while a card is up the pointer is watched directly, and the card closes
     as soon as the pointer is anywhere but the control — whatever did or did
     not fire on the way. The listeners exist only while it is open, so there
     is no cost to the rest of the page. */
  useEffect(() => {
    if (!show) {
      return undefined;
    }
    const closeIfPointerAway = (event) => {
      const target = targetRef.current;
      if (target && !target.contains(event.target)) {
        hideTooltip();
      }
    };
    document.addEventListener("mousemove", closeIfPointerAway);
    document.addEventListener("wheel", closeIfPointerAway, { passive: true });
    return () => {
      document.removeEventListener("mousemove", closeIfPointerAway);
      document.removeEventListener("wheel", closeIfPointerAway);
    };
  }, [show]);

  useEffect(
    () => () => {
      if (activeTooltipController === tooltipController.current) {
        activeTooltipController = null;
      }
    },
    [],
  );

  /* Dropping the text is how a caller suppresses the tooltip while something
     else is on screen — the notification bell blanks it while its panel is
     open. That takes the trigger away with it, and the trigger is what was
     carrying onMouseLeave, so the pointer can leave the control with nothing
     left to hear about it. `show` would still be true when the text came
     back, and the tooltip would reappear on its own with the pointer nowhere
     near it. Hiding here is what closes that gap. */
  useEffect(() => {
    if (!text) {
      setShow(false);
      if (activeTooltipController === tooltipController.current) {
        activeTooltipController = null;
      }
    }
  }, [text]);

  // Nothing to explain: render the control untouched rather than an empty
  // tooltip that follows the pointer around.
  if (!text) {
    return children;
  }

  const tipId = `help-tip-${generatedId}`;

  return (
    <OverlayTrigger
      placement={placement}
      show={show}
      onToggle={handleToggle}
      popperConfig={TOOLTIP_POPPER_CONFIG}
      /* Out of the page entirely: rendered inline, a tooltip inside a
         container that hides its overflow — a table wrapper, an off-canvas
         panel — gets clipped and squeezed instead of floating over it. */
      container={getTooltipLayer()}
      overlay={
        <Tooltip
          id={tipId}
          className={`help-tip-overlay ${overlayClassName}`.trim()}
        >
          {text}
        </Tooltip>
      }
    >
      {children ? (
        <span
          ref={targetRef}
          className={`help-tip-target ${wrapperClassName}`.trim()}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={hideTooltip}
          onMouseDown={handleMouseDown}
          onFocus={() => handleToggle(true)}
          onBlur={handleBlur}
        >
          {children}
        </span>
      ) : (
        <span
          ref={targetRef}
          className={`help-tip-icon ${className}`.trim()}
          tabIndex={0}
          role="button"
          aria-label="Help"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={hideTooltip}
          onMouseDown={handleMouseDown}
          onFocus={() => handleToggle(true)}
          onBlur={handleBlur}
        >
          {/* Outlined rather than filled: it sits beside a label as a quiet
              offer of help, not as a status mark on the field. */}
          <BsInfoCircle size={iconSize} />
        </span>
      )}
    </OverlayTrigger>
  );
};

HelpTip.propTypes = {
  /** The explanation shown on hover/focus. Falsy renders no tooltip at all. */
  text: PropTypes.node,
  placement: PropTypes.string,
  /** Extra classes for the info icon (icon form only). */
  className: PropTypes.string,
  iconSize: PropTypes.number,
  /** Extra classes for the wrapper around children (wrapping form only). */
  wrapperClassName: PropTypes.string,
  /** Extra classes for the card itself, for help that is more than a sentence. */
  overlayClassName: PropTypes.string,
  children: PropTypes.node,
};

export default HelpTip;
